import React, { useEffect, useRef, useState } from 'react'
import { ArrowLeft, At, Check, CheckCircle, PencilSimple, Plus, X } from '@phosphor-icons/react'
import { useAccountPageUIContext } from '../AccountPageProvider'
import { AccountPageOverlayPanel } from './AccountPageChrome'

// ─── Internal types ───────────────────────────────────────────────────────────

type EmailOverlayScreen =
  | { type: 'EMAIL_LIST' }
  | { type: 'ADD_EMAIL' }
  | { type: 'VERIFY_EMAIL'; email: string; contextId: string; isNew: boolean }
  | { type: 'EMAIL_VERIFIED' }

interface AccountEmailFlowOverlayProps {
  returnToOrders: boolean
  /** When set, skips the email list and directly sends OTP to this email on open */
  initialEmail?: string
  onClose: () => void
  onComplete: (returnToOrders: boolean) => void
}

// ─── OTP Box Input ────────────────────────────────────────────────────────────

const OTPBoxInput = ({
  value,
  onChange,
  disabled,
  hasError,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  hasError?: boolean
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, digit: string) => {
    const clean = digit.replace(/\D/g, '').slice(-1)
    const chars = value.padEnd(4, ' ').split('')
    chars[index] = clean || ' '
    const newVal = chars.join('').trimEnd()
    onChange(newVal)
    if (clean && index < 3) inputRefs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        const chars = value.split('')
        chars[index] = ''
        onChange(chars.join(''))
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus()
        const chars = value.split('')
        chars[index - 1] = ''
        onChange(chars.join(''))
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4)
    onChange(pasted)
    const focusIndex = Math.min(pasted.length, 3)
    inputRefs.current[focusIndex]?.focus()
  }

  return (
    <div className='ui-flex ui-gap-3 ui-justify-center'>
      {[0, 1, 2, 3].map(i => (
        <input
          key={i}
          ref={el => { inputRefs.current[i] = el }}
          type='tel'
          inputMode='numeric'
          maxLength={1}
          value={value[i] ?? ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          autoFocus={i === 0}
          className={`ui-h-12 ui-w-12 ui-text-center ui-text-base ui-font-medium ui-rounded-[10px] ui-outline-none ui-bg-[rgba(0,0,0,0.04)] ui-transition-shadow ${
            hasError
              ? 'ui-shadow-[0_0_0_1.5px_#ef4444,0_1px_3px_0_rgba(26,26,26,0.08)]'
              : 'ui-shadow-[0_0_0_1px_rgba(106,114,130,0.24),0_1px_3px_0_rgba(26,26,26,0.08)] focus:ui-shadow-[0_0_0_1.5px_#4A5565,0_1.5px_4px_0_rgba(9,9,11,0.12)]'
          } ${disabled ? 'ui-opacity-50' : ''}`}
        />
      ))}
    </div>
  )
}

// ─── EmailList screen ─────────────────────────────────────────────────────────

const EmailListScreen = ({
  emailIdentifiers,
  primaryEmail,
  onSelectEmail,
  onAddEmail,
  onBack,
  onClose,
  isLoading,
}: {
  emailIdentifiers: { oid: string; verified: boolean }[]
  primaryEmail?: string
  onSelectEmail: (email: string, verified: boolean) => void
  onAddEmail: () => void
  onBack: () => void
  onClose: () => void
  isLoading: boolean
}) => {
  const splitEmail = (email: string) => {
    const at = email.lastIndexOf('@')
    if (at === -1) return { local: email, domain: '' }
    return { local: email.slice(0, at), domain: email.slice(at) }
  }

  return (
    <div className='ui-flex ui-w-full ui-flex-col'>
      {/* Sub-header */}
      <div className='ui-flex ui-flex-col ui-items-start ui-justify-center ui-px-5 ui-pt-4'>
        <div className='ui-flex ui-w-full ui-items-center ui-gap-3'>
          <button
            onClick={onBack}
            className='ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-rounded-lg ui-border ui-border-[rgba(0,0,0,0.12)] ui-bg-white ui-p-1.5 ui-shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]'
          >
            <div className='ui-flex ui-h-6 ui-w-6 ui-items-center ui-justify-center'>
              <ArrowLeft size={20} weight='regular' className='ui-text-zinc-800' />
            </div>
          </button>
          <div className='ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-items-start'>
            <div className='ui-flex ui-items-center ui-gap-1'>
              <div className='ui-flex ui-h-6 ui-w-6 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-lg'>
                <At size={20} weight='fill' color='#ea6b2d' />
              </div>
              <span className='ui-text-[15px] ui-font-semibold ui-text-zinc-800 ui-whitespace-nowrap'>
                {emailIdentifiers.length} email{emailIdentifiers.length !== 1 ? 's' : ''} found
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className='ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-w-7 ui-h-7 ui-rounded-full ui-bg-[rgba(0,0,0,0.06)] hover:ui-bg-[rgba(0,0,0,0.1)] ui-transition-colors'
          >
            <X size={13} weight='bold' />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className='ui-flex ui-flex-col ui-items-start'>
        <div className='ui-flex ui-w-full ui-flex-col ui-items-start ui-px-4 ui-pb-4'>
          {/* Subtitle */}
          <div className='ui-flex ui-items-center ui-justify-center ui-px-3 ui-py-2'>
            <p className='ui-text-[12px] ui-font-medium ui-text-zinc-500'>
              Your orders are tied to the email used at checkout. Select to switch.
            </p>
          </div>

          {/* Email list */}
          <div className='ui-flex ui-w-full ui-flex-col ui-items-start ui-overflow-hidden ui-pt-1'>
            <div className='ui-flex ui-max-h-[270px] ui-w-full ui-flex-col ui-items-start ui-overflow-y-auto ui-scrollbar-hide'>
              {emailIdentifiers.map(id => {
                const isPrimary = id.oid === primaryEmail
                const { local, domain } = splitEmail(id.oid)
                return (
                  <button
                    key={id.oid}
                    onClick={() => !isLoading && onSelectEmail(id.oid, id.verified)}
                    disabled={isLoading}
                    className={`ui-flex ui-w-full ui-items-center ui-gap-2 ui-rounded-xl ui-px-2 ui-py-2.5 ui-text-left ui-transition-colors ${
                      isPrimary ? 'ui-bg-[rgba(113,113,123,0.05)]' : 'hover:ui-bg-[rgba(113,113,123,0.03)]'
                    } ui-disabled:ui-opacity-50`}
                  >
                    {/* Avatar */}
                    <div className='ui-flex ui-h-7 ui-w-7 ui-shrink-0 ui-items-center ui-justify-center ui-overflow-hidden ui-rounded-full ui-bg-[rgba(0,0,0,0.08)]'>
                      <span className='ui-text-xs ui-font-semibold ui-uppercase ui-tracking-[0.04em] ui-text-zinc-800 ui-opacity-50'>
                        {id.oid.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    {/* Email text */}
                    <div className='ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-items-start ui-justify-center ui-gap-0.5'>
                      <p className='ui-truncate ui-text-[14px] ui-font-medium ui-text-zinc-800'>
                        <span>{local}</span>
                        <span className='ui-text-[14px] ui-font-normal ui-text-zinc-500'>{domain}</span>
                      </p>
                      {!id.verified && (
                        <span className='ui-text-[11px] ui-font-medium ui-text-amber-500'>Unverified</span>
                      )}
                    </div>
                    {/* Primary check */}
                    {isPrimary && id.verified && (
                      <div className='ui-flex ui-h-7 ui-w-7 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-lg'>
                        <Check size={16} weight='bold' className='ui-text-zinc-800' />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Add new email */}
            <div className='ui-w-full ui-pt-3'>
              <button
                onClick={onAddEmail}
                disabled={isLoading}
                className='ui-flex ui-h-11 ui-w-full ui-items-center ui-gap-2 ui-rounded-lg ui-border ui-border-[rgba(0,0,0,0.1)] ui-bg-[rgba(0,0,0,0.04)] ui-px-2 ui-py-2.5 ui-disabled:ui-opacity-50'
              >
                <div className='ui-relative ui-flex ui-h-7 ui-w-7 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-[9.6px] ui-opacity-60'>
                  <Plus size={20} weight='regular' className='ui-text-zinc-800' />
                </div>
                <div className='ui-flex ui-min-w-0 ui-flex-1 ui-items-center'>
                  <p className='ui-min-w-0 ui-flex-1 ui-truncate ui-text-[14px] ui-font-medium ui-text-zinc-800'>
                    Add new email
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── AddEmail screen ──────────────────────────────────────────────────────────

const AddEmailScreen = ({
  onBack,
  onContinue,
  onClose,
  isLoading,
}: {
  onBack: () => void
  onContinue: (email: string) => void
  onClose: () => void
  isLoading: boolean
}) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)

  const handleSubmit = () => {
    const trimmed = email.trim()
    if (!trimmed) { setError('Email is required'); return }
    if (!isValidEmail(trimmed)) { setError('Enter a valid email address'); return }
    onContinue(trimmed)
  }

  return (
    <div className='ui-flex ui-w-full ui-flex-col'>
      {/* Sub-header */}
      <div className='ui-flex ui-items-start ui-justify-between ui-px-6 ui-pb-3 ui-pt-4'>
        <div className='ui-flex ui-flex-col ui-gap-0.5'>
          <span className='ui-text-[16px] ui-font-semibold ui-text-zinc-800'>Almost there</span>
          <span className='ui-text-[14px] ui-font-normal ui-text-zinc-500'>
            Add an email to link your orders.
          </span>
        </div>
        <button
          onClick={onClose}
          className='ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-w-7 ui-h-7 ui-rounded-full ui-bg-[rgba(0,0,0,0.06)] hover:ui-bg-[rgba(0,0,0,0.1)] ui-transition-colors'
        >
          <X size={13} weight='bold' />
        </button>
      </div>

      {/* Body */}
      <div className='ui-flex ui-flex-col ui-gap-3 ui-p-4'>
        <div className='ui-flex ui-flex-col ui-gap-1.5'>
          <label className='ui-px-1 ui-text-[12px] ui-font-medium ui-text-zinc-500'>
            Email address
          </label>
          <div className='ui-rounded-xl ui-border ui-border-[rgba(9,9,11,0.1)] ui-bg-[rgba(0,0,0,0.04)] ui-px-3 ui-py-2'>
            <input
              type='email'
              value={email}
              onChange={e => { setEmail(e.target.value); if (error) setError('') }}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              placeholder='you@example.com'
              autoFocus
              disabled={isLoading}
              className='ui-h-[38px] ui-w-full ui-bg-transparent ui-text-[14px] ui-font-medium ui-text-zinc-800 ui-outline-none placeholder:ui-text-zinc-400'
            />
          </div>
          {error && <p className='ui-px-1 ui-text-[12px] ui-font-medium ui-text-red-500'>{error}</p>}
        </div>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !email.trim()}
          className='ui-h-11 ui-w-full ui-rounded-xl ui-bg-[#27272a] ui-text-[14px] ui-font-semibold ui-text-white ui-transition-opacity disabled:ui-opacity-40'
        >
          {isLoading ? 'Sending…' : 'Continue'}
        </button>
      </div>
    </div>
  )
}

// ─── VerifyEmail screen ───────────────────────────────────────────────────────

const VerifyEmailScreen = ({
  email,
  onVerify,
  onResend,
  onBack,
  onClose,
  isLoading,
}: {
  email: string
  onVerify: (otp: string) => Promise<void>
  onResend: () => void
  onBack: () => void
  onClose: () => void
  isLoading: boolean
}) => {
  const [otp, setOtp] = useState('')
  const [hasError, setHasError] = useState(false)
  const [counter, setCounter] = useState(30)
  const [counterExpired, setCounterExpired] = useState(false)

  useEffect(() => {
    if (counter === 0) { setCounterExpired(true); return }
    const t = setInterval(() => setCounter(c => c - 1), 1000)
    return () => clearInterval(t)
  }, [counter])

  const maskEmail = (e: string) => {
    const at = e.lastIndexOf('@')
    if (at === -1) return e
    const local = e.slice(0, at)
    const domain = e.slice(at)
    const start = Math.min(3, Math.floor(local.length / 2))
    const end = Math.min(2, local.length - start)
    return `${local.slice(0, start)}***${end > 0 ? local.slice(local.length - end) : ''}${domain}`
  }

  const handleVerify = async () => {
    if (otp.length !== 4 || isLoading) return
    try {
      await onVerify(otp)
    } catch {
      setHasError(true)
      setOtp('')
    }
  }

  const handleResend = () => {
    setOtp('')
    setHasError(false)
    setCounter(30)
    setCounterExpired(false)
    onResend()
  }

  useEffect(() => {
    if (otp.length === 4) handleVerify()
  }, [otp])

  return (
    <div className='ui-flex ui-w-full ui-flex-col'>
      {/* Sub-header */}
      <div className='ui-flex ui-items-start ui-justify-between ui-px-6 ui-pb-3 ui-pt-4'>
        <div className='ui-flex ui-flex-col ui-gap-0.5'>
          <span className='ui-text-[16px] ui-font-semibold ui-text-zinc-800'>Almost there</span>
          <span className='ui-text-[14px] ui-font-normal ui-text-zinc-500'>
            Verify your email address
          </span>
        </div>
        <button
          onClick={onClose}
          className='ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-w-7 ui-h-7 ui-rounded-full ui-bg-[rgba(0,0,0,0.06)] hover:ui-bg-[rgba(0,0,0,0.1)] ui-transition-colors'
        >
          <X size={13} weight='bold' />
        </button>
      </div>

      {/* Body */}
      <div className='ui-flex ui-flex-col ui-gap-4 ui-p-4'>
        <div className='ui-flex ui-w-full ui-flex-col ui-gap-4'>
          {/* Masked email + edit */}
          <div className='ui-flex ui-items-center ui-gap-1.5'>
            <p className='ui-text-[14px] ui-font-medium ui-text-zinc-500'>
              Code sent to{' '}
              <span className='ui-font-semibold ui-text-zinc-800'>{maskEmail(email)}</span>
            </p>
            <button onClick={onBack} className='ui-flex ui-shrink-0 ui-items-center ui-justify-center'>
              <PencilSimple size={16} weight='regular' className='ui-text-zinc-500' />
            </button>
          </div>

          <OTPBoxInput
            value={otp}
            onChange={setOtp}
            disabled={isLoading}
            hasError={hasError}
          />

          {hasError && (
            <p className='ui-text-center ui-text-[12px] ui-font-medium ui-text-red-500'>
              Incorrect code. Please try again.
            </p>
          )}

          {/* Resend */}
          <div className='ui-flex ui-items-start ui-gap-1'>
            <span className='ui-text-[14px] ui-font-medium ui-text-zinc-800 ui-opacity-70'>
              Didn't receive it?
            </span>
            {counterExpired ? (
              <button
                onClick={handleResend}
                disabled={isLoading}
                className='ui-text-[14px] ui-font-semibold ui-text-zinc-800 disabled:ui-opacity-50'
              >
                Resend
              </button>
            ) : (
              <span className='ui-text-[14px] ui-font-semibold ui-text-zinc-800 ui-opacity-50'>
                Resend in {counter}s
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleVerify}
          disabled={isLoading || otp.length !== 4}
          className='ui-h-11 ui-w-full ui-rounded-xl ui-bg-[#27272a] ui-text-[14px] ui-font-semibold ui-text-white ui-transition-opacity disabled:ui-opacity-40'
        >
          {isLoading ? 'Verifying…' : 'Verify & Continue'}
        </button>
      </div>
    </div>
  )
}

// ─── EmailVerified screen ─────────────────────────────────────────────────────

const EmailVerifiedScreen = ({ onComplete }: { onComplete: () => void }) => {
  useEffect(() => {
    const t = setTimeout(onComplete, 1200)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className='ui-flex ui-flex-col ui-items-center ui-justify-center ui-py-12 ui-gap-3'>
      <CheckCircle size={48} weight='fill' className='ui-text-emerald-500' />
      <p className='ui-text-[16px] ui-font-semibold ui-text-zinc-800'>Email updated</p>
    </div>
  )
}

// ─── Main overlay component ───────────────────────────────────────────────────

const AccountEmailFlowOverlay: React.FC<AccountEmailFlowOverlayProps> = ({
  returnToOrders,
  initialEmail,
  onClose,
  onComplete,
}) => {
  const { data, actions } = useAccountPageUIContext()
  const emailIdentifiers = data?.profilePage?.user?.emailIdentifiers ?? []

  const [screen, setScreen] = useState<EmailOverlayScreen>({ type: 'EMAIL_LIST' })
  const [isLoading, setIsLoading] = useState(false)

  // Runs once on mount — component is only rendered when the overlay should be open
  useEffect(() => {
    if (initialEmail) {
      setIsLoading(true)
      actions.onSendEmailOTP?.(initialEmail).then(result => {
        if (result?.contextId) {
          setScreen({ type: 'VERIFY_EMAIL', email: initialEmail, contextId: result.contextId, isNew: false })
        } else {
          setScreen({ type: 'EMAIL_LIST' })
        }
      }).catch(() => {
        setScreen({ type: 'EMAIL_LIST' })
      }).finally(() => {
        setIsLoading(false)
      })
    } else {
      setScreen(emailIdentifiers.length === 0 ? { type: 'ADD_EMAIL' } : { type: 'EMAIL_LIST' })
    }
  }, [])

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleSelectEmail = async (email: string, verified: boolean) => {
    if (verified) {
      setIsLoading(true)
      try {
        await actions.onSetPrimaryEmail?.(email)
        setScreen({ type: 'EMAIL_VERIFIED' })
      } finally {
        setIsLoading(false)
      }
      return
    }
    setIsLoading(true)
    try {
      const result = await actions.onSendEmailOTP?.(email)
      if (result?.contextId) {
        setScreen({ type: 'VERIFY_EMAIL', email, contextId: result.contextId, isNew: false })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddEmailContinue = async (email: string) => {
    setIsLoading(true)
    try {
      const result = await actions.onSendEmailOTP?.(email)
      if (result?.contextId) {
        setScreen({ type: 'VERIFY_EMAIL', email, contextId: result.contextId, isNew: true })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOTP = async (otp: string) => {
    if (screen.type !== 'VERIFY_EMAIL') return
    setIsLoading(true)
    try {
      await actions.onVerifyEmailOTP?.(screen.contextId, otp)
      await actions.onSetPrimaryEmail?.(screen.email)
      setScreen({ type: 'EMAIL_VERIFIED' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (screen.type !== 'VERIFY_EMAIL') return
    setIsLoading(true)
    try {
      const result = await actions.onSendEmailOTP?.(screen.email)
      if (result?.contextId) {
        setScreen(prev => prev.type === 'VERIFY_EMAIL' ? { ...prev, contextId: result.contextId } : prev)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    if (screen.type === 'VERIFY_EMAIL') {
      setScreen(screen.isNew ? { type: 'ADD_EMAIL' } : { type: 'EMAIL_LIST' })
    } else if (screen.type === 'ADD_EMAIL' && emailIdentifiers.length > 0) {
      setScreen({ type: 'EMAIL_LIST' })
    } else {
      onClose()
    }
  }

  const primaryEmail = data?.profilePage?.user?.primaryEmail

  return (
    <AccountPageOverlayPanel onClose={onClose}>
      {screen.type === 'EMAIL_LIST' && (
        <EmailListScreen
          emailIdentifiers={emailIdentifiers}
          primaryEmail={primaryEmail}
          onSelectEmail={handleSelectEmail}
          onAddEmail={() => setScreen({ type: 'ADD_EMAIL' })}
          onBack={onClose}
          onClose={onClose}
          isLoading={isLoading}
        />
      )}
      {screen.type === 'ADD_EMAIL' && (
        <AddEmailScreen
          onBack={handleBack}
          onContinue={handleAddEmailContinue}
          onClose={onClose}
          isLoading={isLoading}
        />
      )}
      {screen.type === 'VERIFY_EMAIL' && (
        <VerifyEmailScreen
          email={screen.email}
          onVerify={handleVerifyOTP}
          onResend={handleResendOTP}
          onBack={handleBack}
          onClose={onClose}
          isLoading={isLoading}
        />
      )}
      {screen.type === 'EMAIL_VERIFIED' && (
        <EmailVerifiedScreen onComplete={() => onComplete(returnToOrders)} />
      )}
    </AccountPageOverlayPanel>
  )
}

export default AccountEmailFlowOverlay
